import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, RotateCcw, AlertCircle, ChevronDown, ChevronRight, Search } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, SimHeader } from "@/components/sim/shared";
import { AdminNav } from "@/components/neusim/AdminNav";
import { apiCall, ApiError } from "@/lib/api";

interface AuditRow {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  action: string;
  target: { type: string; id: string | null; label: string | null };
  before: unknown;
  after: unknown;
  ipAddress: string | null;
  userAgent: string | null;
}

interface AuditResponse {
  total: number;
  limit: number;
  offset: number;
  rows: AuditRow[];
}

function actionTone(action: string): { bg: string; border: string; text: string } {
  if (action.includes("purge") || action.includes("delete") || action.includes("reset")) {
    return { bg: "rgba(248,114,114,0.10)", border: "rgba(248,114,114,0.45)", text: "#fca5a5" };
  }
  if (action.includes("archive") || action.includes("remove") || action.includes("revoke")) {
    return { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.45)", text: "#fcd34d" };
  }
  if (action.includes("create") || action.includes("add") || action.includes("grant") || action.includes("invite")) {
    return { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.45)", text: "#86efac" };
  }
  return { bg: "rgba(167,139,250,0.10)", border: "rgba(167,139,250,0.45)", text: "#c4b5fd" };
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function AdminAuditPage(): JSX.Element {
  const [data, setData] = useState<AuditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Filters (debounced via the [query] effect dep).
  const [query, setQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall<AuditResponse>("/libraryAuditLog", {
        method: "POST",
        timeoutMs: 120_000,
        body: { query, action: actionFilter, offset, limit: LIMIT },
      });
      setData(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to load audit log");
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch on filter change (with tiny debounce on query).
  useEffect(() => {
    const t = window.setTimeout(() => { void load(); }, 250);
    return () => window.clearTimeout(t);
  }, [query, actionFilter, offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string): void => {
    setExpanded((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const knownActionPrefixes = useMemo(
    () => ["user", "cohort", "client", "project", "sim", "attempt", "assignment", "client_admin_projects", "entra"],
    [],
  );

  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const lastShown = Math.min(offset + LIMIT, total);

  return (
    <ScreenShell>
      <SimHeader decision={total} xp={0} />
      <main className="mx-auto max-w-6xl px-4 pb-20 md:px-8">
        <div className="mb-5"><AdminNav /></div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div>
            <div className="flex items-center gap-2 text-emerald-300">
              <ShieldCheck className="h-4 w-4" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Audit log</span>
            </div>
            <h1 className="mt-2 text-[28px] font-bold leading-tight">
              <GradientText>Who did what, when</GradientText>
            </h1>
            <p className="mt-1 text-sm text-white/55">
              Every admin write (user / cohort / sim / project / client / Entra invite + delete) lands here. Append-only — rows cannot be edited.
            </p>
          </div>
        </motion.div>

        {/* Filters */}
        <GlassCard className="mt-6 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex flex-1 items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm">
              <Search className="h-3.5 w-3.5 text-white/40" />
              <input
                type="text" placeholder="Search by actor email or target label"
                value={query} onChange={(e) => { setQuery(e.target.value); setOffset(0); }}
                className="flex-1 bg-transparent text-white outline-none placeholder:text-white/30"
              />
            </label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setOffset(0); }}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none"
            >
              <option value="" className="bg-neusim-bg">All actions</option>
              {knownActionPrefixes.map((p) => (
                <option key={p} value={p} className="bg-neusim-bg">{p}.*</option>
              ))}
            </select>
            <button
              type="button" onClick={() => void load()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-xs font-medium text-white/80 hover:border-white/30 hover:bg-white/10"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reload
            </button>
          </div>
        </GlassCard>

        {loading && (
          <div className="flex min-h-[30vh] flex-col items-center justify-center text-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} className="h-10 w-10 rounded-full border-2 border-white/10 border-t-emerald-400" />
            <p className="mt-4 text-sm text-white/55">Loading audit log…</p>
          </div>
        )}

        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-8 max-w-lg p-8 text-center">
            <AlertCircle className="mx-auto h-9 w-9 text-[#f87272]" />
            <p className="mt-3 text-sm text-white/70">{error}</p>
            <button type="button" onClick={() => void load()} className="mt-5 inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10">
              <RotateCcw className="h-3.5 w-3.5" /> Try again
            </button>
          </GlassCard>
        )}

        {!loading && error === null && rows.length === 0 && (
          <GlassCard className="mt-6 p-10 text-center">
            <p className="text-sm text-white/55">No audit events match your filters yet.</p>
          </GlassCard>
        )}

        {!loading && error === null && rows.length > 0 && (
          <>
            <div className="mt-4 flex items-center justify-between text-xs text-white/55">
              <div>
                Showing <span className="font-mono text-white/80">{offset + 1}</span>–<span className="font-mono text-white/80">{lastShown}</span> of <span className="font-mono text-white/80">{total}</span>
              </div>
              <div className="flex gap-1.5">
                <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                        className="rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] disabled:opacity-40 hover:bg-white/10">
                  ← Prev
                </button>
                <button type="button" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}
                        className="rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] disabled:opacity-40 hover:bg-white/10">
                  Next →
                </button>
              </div>
            </div>

            <GlassCard className="mt-3 p-0">
              <table className="w-full table-fixed text-[12.5px]">
                <colgroup>
                  <col style={{ width: "2rem" }} />
                  <col style={{ width: "11rem" }} />
                  <col style={{ width: "11rem" }} />
                  <col style={{ width: "13rem" }} />
                  <col />
                </colgroup>
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.12em] text-white/40">
                    <th></th>
                    <th className="px-3 py-2.5 text-left">When</th>
                    <th className="px-3 py-2.5 text-left">Actor</th>
                    <th className="px-3 py-2.5 text-left">Action</th>
                    <th className="px-3 py-2.5 text-left">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const tone = actionTone(r.action);
                    const isOpen = expanded.has(r.id);
                    return (
                      <RowFrag key={r.id}>
                        <tr onClick={() => toggle(r.id)} className="cursor-pointer border-b border-white/5 hover:bg-white/[0.03]">
                          <td className="px-1 py-2.5 text-center text-white/40">
                            {isOpen ? <ChevronDown className="inline h-3.5 w-3.5" /> : <ChevronRight className="inline h-3.5 w-3.5" />}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-[11px] text-white/65">{fmtTime(r.occurredAt)}</td>
                          <td className="px-3 py-2.5 truncate">
                            <div className="truncate text-white">{r.actorEmail ?? <span className="text-white/30">— deleted —</span>}</div>
                            <div className="truncate text-[10.5px] text-white/45">{r.actorRole ?? ""}</div>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold"
                                  style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}>
                              {r.action}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 truncate">
                            <div className="truncate text-white/80">{r.target.label ?? <span className="text-white/40">{r.target.type} · {r.target.id ?? "—"}</span>}</div>
                            <div className="truncate text-[10.5px] text-white/45">{r.target.type}</div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-white/5 bg-white/[0.02]">
                            <td></td>
                            <td colSpan={4} className="px-3 py-3">
                              <div className="grid gap-3 md:grid-cols-2">
                                <Snapshot title="Before" value={r.before} />
                                <Snapshot title="After"  value={r.after}  />
                              </div>
                              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-white/55">
                                {r.ipAddress !== null && <span><span className="text-white/35">IP:</span> <span className="font-mono">{r.ipAddress}</span></span>}
                                {r.userAgent !== null && <span className="truncate"><span className="text-white/35">UA:</span> <span className="font-mono">{r.userAgent}</span></span>}
                                <span><span className="text-white/35">Event id:</span> <span className="font-mono">{r.id}</span></span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </RowFrag>
                    );
                  })}
                </tbody>
              </table>
            </GlassCard>
          </>
        )}
      </main>
    </ScreenShell>
  );
}

function RowFrag({ children }: { children: React.ReactNode }): JSX.Element { return <>{children}</>; }

function Snapshot({ title, value }: { title: string; value: unknown }): JSX.Element {
  if (value === null || value === undefined) {
    return (
      <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">{title}</div>
        <div className="text-[11px] italic text-white/35">— none —</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">{title}</div>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[10.5px] leading-relaxed text-white/75">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
